import type { ScheduleWorkdayView } from "@planerius/contracts/cabinet"

import type { BranchSettingsView, CatalogView, EmployeeView } from "../../../api/directory.api"
import { countOf as plural } from "../../../lib/plural"

/**
 * 4-А2 «Путь до первой записи» — шесть обязательных шагов и прогресс.
 *
 * ОТКУДА БЕРЁТСЯ ПРИЗНАК «СДЕЛАНО». Отдельного маршрута чек-листа в API нет — это
 * названный разрыв этапа. Но все шесть шагов проверяемы из справочников, которые API
 * отдаёт уже сейчас: филиал, часы, сотрудники, услуги, связка «кто что делает» и смены
 * на неделю. Поэтому чек-лист СЧИТАЕТСЯ, а не хранится, и это не временный костыль:
 * счётчик, который хранится отдельно от данных, рано или поздно расходится с ними —
 * и владелец видит «готово 6 из 6» на салоне без единой смены.
 *
 * ПОЧЕМУ ПОДПИСЬ ШАГА НАЗЫВАЕТ ПРЕПЯТСТВИЕ, А НЕ ПОВТОРЯЕТ ЗАГОЛОВОК. «У Дмитрия услуги
 * не отмечены» — это следующее действие; «не выполнено» — это отписка. Владелец в первый
 * вечер не знает, что такое «связка исполнителей», и обязан узнать это из строки, которую
 * читает.
 */

export interface ChecklistStep {
  readonly id: ChecklistStepId
  readonly title: string
  /** Что именно уже есть — или чего не хватает. */
  readonly detail: string
  readonly done: boolean
  /** «Изменить» у сделанного, «Заполнить» у несделанного. Из макета. */
  readonly action: string
  /** Куда ведёт строка. Пусто — раздела для этого шага в кабинете ещё нет. */
  readonly path: string | null
}

export type ChecklistStepId =
  | "branch"
  | "hours"
  | "employees"
  | "services"
  | "employee-services"
  | "schedule"

export interface ChecklistView {
  readonly steps: readonly ChecklistStep[]
  readonly doneCount: number
  readonly total: number
  /** Строка над полосой прогресса. */
  readonly progressText: string
  /** Заполнение полосы, 0–100. */
  readonly progressPercent: number
  /** Можно ли включать страницу записи. */
  readonly canPublish: boolean
  /** «Не хватает: …» — перечисление строчными, как в макете. Пусто, когда всё готово. */
  readonly missingText: string
}

export interface ChecklistInput {
  readonly branches: readonly BranchSettingsView[]
  readonly catalog: CatalogView
  readonly employees: readonly EmployeeView[]
  /** Смены ближайшей недели. Пустой список — «Смен пока нет». */
  readonly workdays: readonly ScheduleWorkdayView[]
}

const WEEKDAY_SHORT: readonly string[] = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"]

/** «10:00:00» → «10:00». Секунды в часах работы не значат ничего и только шумят. */
function hhmm(time: string): string {
  return time.slice(0, 5)
}

/**
 * Часы работы одной строкой: «пн–сб 10:00–20:00».
 *
 * Дни недели нумерованы как EXTRACT(DOW) в PostgreSQL — 0 = воскресенье. Порядок показа
 * при этом человеческий, с понедельника: неделя, начинающаяся с воскресенья, читается
 * как ошибка, и владелец идёт проверять, не сбились ли часы.
 */
export function formatBranchHours(hours: readonly { weekday: number; opensAt: string; closesAt: string }[]): string {
  if (hours.length === 0) return ""

  const humanOrder = [1, 2, 3, 4, 5, 6, 0]
  const byDay = new Map<number, string>()
  for (const row of hours) {
    const span = `${hhmm(row.opensAt)}–${hhmm(row.closesAt)}`
    const existing = byDay.get(row.weekday)
    // Два интервала в одном дне — это законный перерыв на обед, а не противоречие.
    byDay.set(row.weekday, existing === undefined ? span : `${existing}, ${span}`)
  }

  const parts: string[] = []
  let runStart: number | null = null
  let runEnd: number | null = null
  let runSpan: string | null = null

  const flush = (): void => {
    if (runStart === null || runEnd === null || runSpan === null) return
    const from = WEEKDAY_SHORT[runStart] ?? ""
    const to = WEEKDAY_SHORT[runEnd] ?? ""
    parts.push(runStart === runEnd ? `${from} ${runSpan}` : `${from}–${to} ${runSpan}`)
    runStart = null
    runEnd = null
    runSpan = null
  }

  for (const day of humanOrder) {
    const span = byDay.get(day)
    if (span === undefined) {
      flush()
      continue
    }
    if (runSpan === span) {
      runEnd = day
      continue
    }
    flush()
    runStart = day
    runEnd = day
    runSpan = span
  }
  flush()

  return parts.join(", ")
}

/**
 * ПОЧЕМУ НЕ «У Дмитрия услуги не отмечены», КАК В МАКЕТЕ.
 *
 * Макет написан для одного знакомого имени, а строку собирает программа для любого:
 * родительного падежа в браузере получить нечем — «у Ким», «у Мельниченко-Орлова»
 * и «у Алексей» одинаково неверны, а имя приходит одним полем «как называть».
 * Поэтому имя стоит в именительном, а отношение к нему выражено тире. Смысл строки —
 * «вот кто мешает включить запись» — сохранён целиком и остаётся проверяемым.
 */
function blockerLine(name: string): string {
  return `${name} — услуги не отмечены`
}

export function buildChecklist(input: ChecklistInput): ChecklistView {
  const branch = input.branches.find((item) => item.isActive) ?? input.branches[0] ?? null

  const activeServices = input.catalog.services.filter((service) => service.isActive)
  const pricedServices = activeServices.filter(
    (service) => service.durationMinutes > 0 && service.priceMinor > 0,
  )

  const bookable = input.employees.filter(
    (employee) => employee.isBookable && employee.archivedAt === null,
  )
  const withoutServices = bookable.filter((employee) => employee.serviceIds.length === 0)

  const hoursText = branch === null ? "" : formatBranchHours(branch.hours)

  const steps: readonly ChecklistStep[] = [
    {
      id: "branch",
      title: "Филиал и адрес",
      detail:
        branch === null
          ? "Не заполнено"
          : branch.address === null
            ? `${branch.name} · адреса нет`
            : `${branch.name} · ${branch.address}`,
      done: branch !== null && branch.address !== null,
      action: branch !== null && branch.address !== null ? "Изменить" : "Заполнить",
      path: "/branches",
    },
    {
      id: "hours",
      title: "Часы работы",
      detail: hoursText.length === 0 ? "Не заполнено" : hoursText,
      done: hoursText.length > 0,
      action: hoursText.length > 0 ? "Изменить" : "Заполнить",
      path: "/branches",
    },
    {
      id: "employees",
      title: "Хотя бы один мастер",
      detail:
        bookable.length === 0 ? "Не заполнено" : plural(bookable.length, "мастер", "мастера", "мастеров"),
      done: bookable.length > 0,
      action: bookable.length > 0 ? "Изменить" : "Заполнить",
      path: "/staff",
    },
    {
      id: "services",
      title: "Услуги с ценой и длительностью",
      detail:
        activeServices.length === 0
          ? "Не заполнено"
          : pricedServices.length < activeServices.length
            ? `${plural(activeServices.length, "услуга", "услуги", "услуг")} — у части нет цены или длительности`
            : `${plural(activeServices.length, "услуга", "услуги", "услуг")} — проверьте цены`,
      done: activeServices.length > 0 && pricedServices.length === activeServices.length,
      action: activeServices.length > 0 ? "Изменить" : "Заполнить",
      path: "/catalog",
    },
    {
      id: "employee-services",
      title: "Кто какие услуги делает",
      detail:
        bookable.length === 0
          ? "Не заполнено"
          : withoutServices.length === 0
            ? "У всех мастеров услуги отмечены"
            : withoutServices.length === 1
              ? blockerLine(withoutServices[0]?.displayName ?? "")
              : `Услуги не отмечены у ${plural(withoutServices.length, "мастера", "мастеров", "мастеров")}`,
      done: bookable.length > 0 && withoutServices.length === 0,
      action: bookable.length > 0 && withoutServices.length === 0 ? "Изменить" : "Заполнить",
      path: "/staff",
    },
    {
      id: "schedule",
      title: "График мастеров на неделю",
      detail:
        input.workdays.length === 0
          ? "Смен пока нет"
          : plural(input.workdays.length, "смена на неделю", "смены на неделю", "смен на неделю"),
      done: input.workdays.length > 0,
      action: input.workdays.length > 0 ? "Изменить" : "Заполнить",
      path: "/staff/schedule",
    },
  ]

  const doneCount = steps.filter((step) => step.done).length
  const total = steps.length
  const missing = steps.filter((step) => !step.done).map((step) => step.title.toLowerCase())

  return {
    steps,
    doneCount,
    total,
    progressText:
      doneCount === 0
        ? "Первые три шага занимают 10 минут. Запись включится после шестого."
        : `Готово ${doneCount} из ${total} · запись включится после шестого`,
    progressPercent: Math.round((doneCount / total) * 100),
    canPublish: doneCount === total,
    missingText: missing.join(", "),
  }
}

/** Необязательные шаги. Данных под них нет ни у одного маршрута — это витрина выгоды. */
export interface OptionalStep {
  readonly title: string
  readonly detail: string
  readonly path: string | null
}

export const OPTIONAL_STEPS: readonly OptionalStep[] = [
  {
    title: "Перенести базу клиентов",
    detail: "Файл из YCLIENTS или DIKIDI — 10 минут",
    path: "/clients",
  },
  {
    title: "Подключить Telegram-бота",
    detail: "Напоминания бесплатно вместо SMS по 5 рублей",
    path: "/channels",
  },
  { title: "Правила записи", detail: "За сколько часов можно отменять", path: "/settings/booking-rules" },
  {
    title: "Пригласить администратора",
    detail: "Доступ в кабинет, без денег и настроек",
    path: "/staff",
  },
]
